import { FC, useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Center, Text } from "@/components/ui";
import { useLocalization, useThemeColors } from "@/hooks";

interface DataPoint {
  x: string;
  y: number;
}

interface BarChartProps {
  minWidth?: number;
  points: DataPoint[];
  totalRespondents?: number;
}

interface BarTooltipEntry {
  count: number;
  name: string;
  value: number;
}

const TICK_COLOR = "hsl(220,10%,46%)";
const GRID_STROKE = "hsl(220,14%,93%)";
const CHARS_PER_LINE = 10;
const LINE_HEIGHT = 13;
const NUMBERED_RE = /^\d+[.)]\s+/;
const BAR_AREA_HEIGHT = 280;

const stripNumber = (s: string) => s.replace(NUMBERED_RE, "");

function wrapWords(text: string): string[] {
  const words = stripNumber(text).split(" ");
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= CHARS_PER_LINE) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      // If a single word is longer than the line, break it hard
      current =
        word.length > CHARS_PER_LINE
          ? `${word.substring(0, CHARS_PER_LINE)}…`
          : word;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, 3);
}

function WrappedXTick({
  x,
  y,
  payload,
}: {
  payload?: { value: string };
  x?: number;
  y?: number;
}) {
  const lines = wrapWords(payload?.value ?? "");
  return (
    <text
      fill={TICK_COLOR}
      fontSize={11}
      textAnchor="middle"
      x={x}
      y={(y ?? 0) + 8}
    >
      {lines.map((line, i) => (
        <tspan dy={i === 0 ? 0 : LINE_HEIGHT} key={i} x={x}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

function BarTooltip({
  active,
  payload,
  formatPercent,
  primary,
}: {
  active?: boolean;
  formatPercent: (v: number) => string;
  payload?: { payload: BarTooltipEntry }[];
  primary: string;
}) {
  if (!active || !payload?.length) return null;
  const { count, name, value } = payload[0].payload;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        padding: "8px 12px",
      }}
    >
      <p
        style={{
          color: "hsl(222,20%,16%)",
          fontSize: 12,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {name}
      </p>
      <p style={{ color: primary, fontSize: 12, margin: "2px 0 0" }}>
        {formatPercent(Math.round(value))} · N={count}
      </p>
    </div>
  );
}

const BarChart: FC<BarChartProps> = ({
  minWidth = 400,
  points,
  totalRespondents,
}) => {
  const { i18n } = useLocalization();
  const themeColors = useThemeColors();

  const { bottomMargin, chartData, chartHeight, chartWidth } = useMemo(() => {
    if (points.length === 0) {
      return {
        bottomMargin: 20,
        chartData: [],
        chartHeight: BAR_AREA_HEIGHT + 40,
        chartWidth: minWidth,
      };
    }

    const aggregated: Record<string, number> = {};
    points.forEach((point) => {
      aggregated[point.x] = (aggregated[point.x] ?? 0) + point.y;
    });

    const labels = Object.keys(aggregated);
    const optionCount = labels.length;

    const maxLines = Math.max(...labels.map((l) => wrapWords(l).length));
    const bottom = maxLines * LINE_HEIGHT + 14;

    const naturalWidth = optionCount * 60 + 120;
    const width = Math.max(naturalWidth, minWidth);

    const rawData = labels.map((x) => ({ x, y: aggregated[x] }));
    const divisor =
      totalRespondents && totalRespondents > 0
        ? totalRespondents
        : rawData.reduce((sum, d) => sum + d.y, 0);

    const data = rawData.map((d) => ({
      count: d.y,
      name: d.x,
      value: divisor > 0 ? (d.y / divisor) * 100 : 0,
    }));

    return {
      bottomMargin: bottom,
      chartData: data,
      chartHeight: BAR_AREA_HEIGHT + 20 + bottom,
      chartWidth: width,
    };
  }, [minWidth, points, totalRespondents]);

  if (chartData.length === 0) {
    return (
      <Center className="w-full" style={{ height: chartHeight }}>
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  const formatPercent = (value: number) =>
    i18n.numberToPercentage(value, { precision: 0 });

  const chartMargin = { bottom: bottomMargin, left: 10, right: 20, top: 20 };

  const sharedChildren = (
    <>
      <CartesianGrid
        stroke={GRID_STROKE}
        strokeDasharray="3 3"
        vertical={false}
      />
      <XAxis
        dataKey="name"
        interval={0}
        tick={<WrappedXTick />}
        tickLine={false}
      />
      <YAxis
        axisLine={false}
        domain={[0, 100]}
        tick={{ fill: TICK_COLOR, fontSize: 11 }}
        tickCount={5}
        tickFormatter={(v) => formatPercent(Math.round(v))}
        tickLine={false}
      />
      <Tooltip
        content={
          <BarTooltip
            formatPercent={formatPercent}
            primary={themeColors.primary}
          />
        }
        cursor={{ fill: "hsl(220,14%,97%)" }}
      />
      <Bar
        animationBegin={0}
        animationDuration={600}
        animationEasing="ease-out"
        dataKey="value"
        fill={themeColors.primary}
        isAnimationActive={true}
        maxBarSize={40}
        radius={[4, 4, 0, 0]}
      />
    </>
  );

  // We always render the chart at its `chartWidth` minimum. When the parent
  // container is wider, ResponsiveContainer stretches the chart up to the
  // parent's width (good for big screens). When the parent is narrower, the
  // outer wrapper overflows horizontally and the user can scroll — matching
  // the behaviour of other section reports (e.g. NPS, Awareness).
  //
  // Note: we use a plain `<div>` for the scroll wrapper rather than a RN
  // `<View>` / `<Box>`, because RN's `ViewStyle` type doesn't expose
  // `overflowX` and RN Web only reliably applies the shorthand `overflow`
  // (which would also clip vertical tooltips). A plain `<div>` is fine here
  // since this file is `.web.tsx`.
  // `minWidth: 0` on the outer scroll wrapper is critical: without it, the
  // div's default `min-width: auto` resolves to its content's intrinsic min
  // size (= `chartWidth`), which propagates up to the enclosing flex item
  // and forces the parent card to grow past its `flex-1` share — breaking
  // the surrounding layout when the available width is smaller than
  // `chartWidth`. With `minWidth: 0`, the wrapper can shrink below the
  // inner content's intrinsic size, so the card stays at its flex share
  // and the overflow scrolls horizontally inside it.
  return (
    <div style={{ minWidth: 0, overflowX: "auto", width: "100%" }}>
      <div style={{ minWidth: chartWidth, width: "100%" }}>
        <ResponsiveContainer height={chartHeight} minWidth={0} width="100%">
          <RechartsBarChart data={chartData} margin={chartMargin}>
            {sharedChildren}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChart;
